import React, { useMemo, useState } from "react";

import {
  SectionEyebrow,
  cx,
  SearchIcon,
} from "../components/CommonComponents";

function CurriculumTable({ semester, dark = false }) {
  if (!semester?.subjects?.length) {
    return (
      <div
        className={cx(
          "rounded-xl border p-8 text-center text-sm",
          dark
            ? "border-white/10 text-white/60"
            : "border-[#071A2F]/10 text-[#65717D]"
        )}
      >
        No subjects available for this semester.
      </div>
    );
  }

  const headers = [
    "Subject",
    "Code",
    "Credits",
    "CT",
    "TA",
    "ESE",
    "Practical",
    "Total",
  ];

  return (
    <div
      className={cx(
        "mt-5 overflow-x-auto border",
        dark ? "border-white/15" : "border-[#071A2F]/10"
      )}
    >
      <table className="w-full overflow-auto text-left text-sm">
        <thead
          className={dark ? "bg-white/10" : "bg-[#071A2F] text-white"}
        >
          <tr>
            {headers.map((header) => (
              <th key={header} className="p-4 font-bold">
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {semester.subjects.map((subject, index) => (
            <tr
              key={`${subject[1] || subject[0]}-${index}`}
              className={cx(
                "border-t",
                dark
                  ? "border-white/10"
                  : "border-[#071A2F]/10"
              )}
            >
              <td className="p-4 font-semibold text-[#071A2F]">
                {subject[0]}
              </td>

              {subject.slice(1).map((value, valueIndex) => (
                <td
                  key={valueIndex}
                  className={cx(
                    "p-4",
                    dark
                      ? "text-white/60"
                      : "text-[#65717D]"
                  )}
                >
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CurriculumStructure({ course }) {
  const [activeSemester, setActiveSemester] = useState(0);
  const [subjectSearch, setSubjectSearch] = useState("");

  const curriculum = course?.curriculum ?? [];

  const currentSemester =
    curriculum[activeSemester] ?? curriculum[0] ?? null;

  const filteredSubjects = useMemo(() => {
    if (!currentSemester?.subjects) {
      return [];
    }

    const term = subjectSearch.trim().toLowerCase();

    if (!term) {
      return currentSemester.subjects;
    }

    return currentSemester.subjects.filter((subject) =>
      subject.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term)
      )
    );
  }, [currentSemester, subjectSearch]);

  if (!course) {
    return null;
  }

  if (!curriculum.length) {
    return (
      <section className="bg-[#F7F2E8] py-20 sm:py-24 lg:py-28">
        <div className="container relative z-[1] mx-auto">
          <div className="rounded-[28px] border border-[#071A2F]/10 bg-white p-8 text-center">
            <p className="text-sm text-[#65717D]">
              Curriculum information is not available for this course.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#F7F2E8] py-20 sm:py-24 lg:py-28">
      <div className="container relative z-[1] mx-auto">
        <header className="grid gap-8 border-b border-[#071A2F]/15 pb-10 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
          <div>
            <SectionEyebrow>
              Curriculum Structure
            </SectionEyebrow>

            <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.8vw,66px)] font-normal leading-[1.05] text-[#071A2F]">
              Explore the academic{" "}
              <em className="text-[#8B1E3F]">
                journey semester by semester.
              </em>
            </h2>
          </div>

          <p className="text-base leading-8 text-[#65717D]">
            Replace the sample subjects with the approved syllabus
            for the selected programme.
          </p>
        </header>

        <div className="mt-10 grid gap-8 lg:grid-cols-[300px_1fr]">
          {/* Semester navigation */}
          <aside className="space-y-2">
            {curriculum.map((semester, index) => (
              <button
                key={`${semester.title}-${index}`}
                type="button"
                onClick={() => {
                  setActiveSemester(index);
                  setSubjectSearch("");
                }}
                className={cx(
                  "flex min-h-[62px] w-full items-center justify-between rounded-2xl px-5 text-left text-sm font-bold transition",
                  activeSemester === index
                    ? "bg-[#071A2F] text-white"
                    : "border border-[#071A2F]/10 bg-white text-[#071A2F] hover:border-[#D5AD57]"
                )}
              >
                <span>{semester.title}</span>

                <span
                  className={
                    activeSemester === index
                      ? "text-[#D5AD57]"
                      : "text-[#8B1E3F]"
                  }
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
              </button>
            ))}
          </aside>

          {/* Curriculum content */}
          <div className="rounded-[28px] border border-[#071A2F]/10 bg-white p-6 sm:p-8">
            <div className="flex flex-col gap-5 border-b border-[#071A2F]/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">
                  Selected Semester
                </span>

                <h3 className="mt-2 font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal text-[#071A2F]">
                  {currentSemester?.title}
                </h3>
              </div>

              <label className="relative block w-full sm:w-[280px]">
                <span className="sr-only">
                  Search subjects
                </span>

                <SearchIcon
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#071A2F]/35"
                />

                <input
                  type="search"
                  value={subjectSearch}
                  onChange={(event) =>
                    setSubjectSearch(event.target.value)
                  }
                  placeholder="Search subjects..."
                  className="h-12 w-full rounded-full border border-[#071A2F]/10 bg-[#F7F2E8] pl-11 pr-4 text-sm text-[#071A2F] outline-none transition placeholder:text-[#65717D]/60 focus:border-[#8B1E3F]"
                />
              </label>
            </div>

            {/* Filtered curriculum table */}
            <CurriculumTable
              semester={{
                ...currentSemester,
                subjects: filteredSubjects,
              }}
            />

            {subjectSearch && filteredSubjects.length === 0 && (
              <p className="mt-5 text-center text-sm text-[#65717D]">
                No subjects found for "{subjectSearch}".
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}